import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AccountAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/accounts", token);

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const [selectedShop, setSelectedShop] = useState("");
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "shop-account-add", selectedShop],
    selectedShop
      ? `shop/currencies/?page_size=all&shop=${selectedShop}`
      : "",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const [name, setName] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0");
  const [note, setNote] = useState("");

  useEffect(() => {
    setCurrencyId("");
  }, [selectedShop, currencyLoading]);

  const handleSubmit = () => {
    handleAdd({
      shop: selectedShop,
      name: name.trim(),
      currency: currencyId,
      opening_balance: openingBalance,
      note: note.trim() || null,
    });
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop("");
      setName("");
      setCurrencyId("");
      setOpeningBalance("0");
      setNote("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("account.addAccount")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("account.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            autoFocus
            margin="dense"
            label={t("account.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.currency")}
            fullWidth
            required
            disabled={!selectedShop || currencyLoading}
            value={currencyId}
            onChange={(e) => setCurrencyId(e.target.value)}
          >
            {!selectedShop ? (
              <MenuItem value="">{t("account.selectShopFirst")}</MenuItem>
            ) : currencyLoading ? (
              <MenuItem value="" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : currencies.length === 0 ? (
              <MenuItem value="" disabled>
                {t("account.noCurrencies")}
              </MenuItem>
            ) : (
              currencies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("account.openingBalance")}
            type="number"
            inputProps={{ step: "0.01" }}
            fullWidth
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("account.note")}
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("account.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedShop || !name.trim() || !currencyId
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("account.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountAdd;
